'use client';

import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';
import dynamic from 'next/dynamic';
import useModal from '../hooks/useModal';
import { LearnDataVideo } from '@osmosis/shared/types/learn';
import { SubProduct } from '@osmosis/shared/types/user';
import { SubProductState } from '@osmosis/shared/types/user';
import { VideoType } from '@osmosis/shared/types/learn';
import { useUserContext } from './UserContext';
import { useLearnData } from './LearnDataProvider';
import { usePathname, useSearchParams } from 'next/navigation';
import { ExpiredUserModalType } from '../constants/ExpiredUserModalConfig';
// import VideoLockoutModal from '../components/learn/VideoLockoutModal';

const LearnLockoutModal = dynamic(
  () => import('../components/learn/LearnLockoutModal'),
  { ssr: false },
);

const AssessmentsLockOutModal = dynamic(
  () => import('../components/learn/AssessmentsLockOutModal'),
  { ssr: false },
);

const VideoLockoutModal = dynamic(
  () => import('../components/learn/VideoLockoutModal'),
  { ssr: false },
);

const ExpiredUserLockoutModal = dynamic(
  () => import('../components/learn/ExpiredUserModal'),
  { ssr: false },
);

type Allow = () => void | Promise<void>;
type Deny = (reason?: DenyReason | null) => void | Promise<void>;

type AccessHandler = (
  onAllow?: Allow | null,
  onDeny?: Deny | null,
  isLogoutFlow?: boolean | undefined,
  onClose?: () => void | null,
) => void;

export enum DenyReason {
  loggedOut = 'loggedOut',
  expired = 'expired',
  lockedProduct = 'lockedProduct',
}

export enum AssessmentsSource {
  flashcard = 'flashcard',
  questions = 'questions',
  notes = 'notes',
}

export type LearnAccessContextProps = {
  handleVideoAccess: AccessHandler;
  handleNotesAccess: AccessHandler;
  handleFlashcardAccess: AccessHandler;
  handleQuestionsAccess: AccessHandler;
  hasAccess: boolean;
  isDemoLearn: boolean;
  hasDMTAccess: boolean;
  denyReason: DenyReason | null;
  lockedSubProduct: LockedSubProduct | null;
  videoType: VideoType;
};

const LearnAccessContext = createContext<LearnAccessContextProps>({
  handleVideoAccess: () => {
    /* no-op */
  },
  handleNotesAccess: () => {
    /* no-op */
  },
  handleFlashcardAccess: () => {
    /* no-op */
  },
  handleQuestionsAccess: () => {
    /* no-op */
  },
  hasAccess: false,
  isDemoLearn: false,
  hasDMTAccess: false,
  denyReason: null,
  lockedSubProduct: null,
  videoType: VideoType.video,
});

type Props = {
  overrides?: LearnDataVideo['overrides'];
  accessState?: LearnDataVideo['accessState'];
  isPrimeOnly?: LearnDataVideo['isPrimeOnly'];
  accessGranted?: boolean;
};

export type LockedSubProduct = SubProduct & { id: string };

/**
 * Returns LearnAccessProvider component.
 * @param {PropsWithChildren<Props>} props Props.
 * @returns {*} {JSX.Element} Return element.
 */
const LearnAccessProvider = (props: PropsWithChildren<Props>): JSX.Element => {
  const {
    children,
    overrides,
    accessState = {},
    isPrimeOnly,
    accessGranted,
  } = props;

  const [hasAccess, setHasAccess] = useState(true);
  const [denyReason, setDenyReason] = useState<DenyReason | null>(null);
  const [lockedSubProduct, setLockedSubProduct] =
    useState<LockedSubProduct | null>(null);

  const [isDemoLearn, setIsDemoLearn] = useState(false);

  const { user, isUserLoaded, hasPrime } = useUserContext();

  const videoLockoutModal = useModal();
  const flashcardLockoutModal = useModal();
  const questionsLockoutModal = useModal();
  const notesLockoutModal = useModal();

  const pathName = usePathname();
  const searchParams = useSearchParams();

  const learnLockoutModal = useModal();

  const { learnData: clientLearnData } = useLearnData();

  const videoType =
    clientLearnData?.isLockedOut && isPrimeOnly && !hasPrime
      ? VideoType.dailyVideoLimitReached
      : VideoType.video;

  const redirect = encodeURIComponent(
    pathName + '?' + searchParams?.toString(),
  );

  useEffect(() => {
    if (!isUserLoaded) return;

    const allowLoggedOutVideo =
      overrides?.allowLoggedOutViewing && !isPrimeOnly;

    setLockedSubProduct(null);

    // allow if video is allowed to be viewed logged out
    if (allowLoggedOutVideo) {
      setIsDemoLearn(true);
    }

    // allow access via authToken (embed/lti)
    if (accessGranted) {
      setHasAccess(true);
      setDenyReason(null);

      return;
    }

    // deny for logged out
    if (!user) {
      setHasAccess(false);
      setDenyReason(DenyReason.loggedOut);

      return;
    }

    const { prime } = user;
    const isB2B = prime?.isB2B;

    const accessStateValues = Object.entries(accessState);
    let productIsActive = false;
    let lockedProduct: LockedSubProduct | null = null;

    accessStateValues.forEach(([key, state]) => {
      productIsActive =
        productIsActive || state.state === SubProductState.active;

      lockedProduct =
        state.state === SubProductState.locked
          ? { ...state, id: key }
          : lockedProduct;
    });

    // deny access for expired
    if (!hasPrime) {
      setHasAccess(false);
      setDenyReason(DenyReason.expired);
      setLockedSubProduct(lockedProduct);
      return;
    }

    // access for b2b - allow if user is B2B
    if (isB2B) {
      setHasAccess(true);
      setDenyReason(null);

      return;
    }

    if (accessStateValues?.length && !productIsActive) {
      setHasAccess(false);
      setDenyReason(DenyReason.lockedProduct);
      setLockedSubProduct(lockedProduct);

      return;
    }

    setHasAccess(true);
  }, [user, hasPrime, isUserLoaded]);

  const handleVideoAccess: AccessHandler = (onAllow, onDeny) => {
    if (isDemoLearn || hasAccess) {
      onAllow?.();
      return;
    }

    onDeny?.(denyReason);
    // Show videoLockoutModal if the deny reason is "expired"
    if (denyReason === DenyReason.expired) {
      videoLockoutModal.show();
    } else {
      // Otherwise, show the learnLockoutModal (e.g., for "loggedOut")
      learnLockoutModal.show();
    }
  };

  const handleAssessmentsAccess = (
    source: AssessmentsSource,
    onAllow?: Allow | null,
    onDeny?: Deny | null,
    isLogoutFlow?: boolean,
    onClose?: () => void | null,
  ) => {
    if (hasAccess || isLogoutFlow) {
      onAllow?.();
      return;
    }

    if (denyReason === DenyReason.expired) {
      switch (source) {
        case AssessmentsSource.flashcard:
          flashcardLockoutModal.show({ onClose });
          break;
        case AssessmentsSource.questions:
          questionsLockoutModal.show({ onClose });
          break;
        case AssessmentsSource.notes:
          notesLockoutModal.show({ onClose });
          break;
      }
      onDeny?.(denyReason);
      return;
    }
    learnLockoutModal.show({ onClose });
    onDeny?.(denyReason);
  };

  const handleNotesAccess: AccessHandler = (onAllow, onDeny) => {
    handleAssessmentsAccess(AssessmentsSource.notes, onAllow, onDeny);
  };

  const handleFlashcardAccess: AccessHandler = (onAllow, onDeny) => {
    handleAssessmentsAccess(AssessmentsSource.flashcard, onAllow, onDeny);
  };

  const handleQuestionsAccess: AccessHandler = (
    onAllow,
    onDeny,
    isLogoutFlow,
    onClose,
  ) => {
    handleAssessmentsAccess(
      AssessmentsSource.questions,
      onAllow,
      onDeny,
      isLogoutFlow,
      onClose,
    );
  };

  const getModalTypeBasedOnPath = (path: string): ExpiredUserModalType => {
    if (path?.includes('/learn-quiz/')) {
      return ExpiredUserModalType.QUIZ;
    }
    return ExpiredUserModalType.LEARN_QUIZ;
  };

  return (
    <LearnAccessContext.Provider
      value={{
        handleVideoAccess,
        handleNotesAccess,
        handleFlashcardAccess,
        handleQuestionsAccess,
        isDemoLearn,
        hasDMTAccess: hasAccess || isDemoLearn,
        hasAccess,
        denyReason,
        lockedSubProduct,
        videoType,
      }}
    >
      {/* video lockout modal */}
      {learnLockoutModal.isVisible && (
        <LearnLockoutModal
          modalProps={learnLockoutModal}
          identifier={'learn'}
        />
      )}
      {videoLockoutModal.isVisible && (
        <VideoLockoutModal
          modalProps={videoLockoutModal}
          lockedSubProduct={lockedSubProduct}
          denyReason={denyReason}
          redirect={redirect}
        />
      )}
      {/* assessments lockout modals */}
      {flashcardLockoutModal.isVisible && (
        <ExpiredUserLockoutModal
          {...flashcardLockoutModal}
          modalType={ExpiredUserModalType.LEARN_QUIZ}
        />
      )}
      {questionsLockoutModal.isVisible && (
        <ExpiredUserLockoutModal
          {...questionsLockoutModal}
          modalType={getModalTypeBasedOnPath(pathName ?? '')}
        />
      )}
      {notesLockoutModal.isVisible && (
        <AssessmentsLockOutModal
          modalProps={notesLockoutModal}
          source={AssessmentsSource.notes}
          redirect={redirect}
        />
      )}
      {children}
    </LearnAccessContext.Provider>
  );
};

/**
 * Returns values of PersonalInfoContext values.
 */
export const useLearnAccess = () => useContext(LearnAccessContext);

export default LearnAccessProvider;
